import type { ErrorCode, FieldError, LoginResponse, ProblemDetails } from "@rema/shared"
import { ERROR_CODES } from "@rema/shared"
import { es } from "../i18n/es"

// Cliente fetch tipado de la consola — docs/05 §7 y contrato de sesión:
// access token SOLO en memoria; refresh token en cookie httpOnly `rema_rt`
// que el navegador adjunta a /v1/auth/* en este mismo origen.

const BASE_URL = "/v1"

export class ApiError extends Error {
  readonly problem: ProblemDetails

  constructor(problem: ProblemDetails) {
    super(problem.detail ?? problem.title)
    this.name = "ApiError"
    this.problem = problem
  }

  get status(): number {
    return this.problem.status
  }

  get code(): ErrorCode {
    return this.problem.code
  }
}

export const isApiError = (error: unknown): error is ApiError => error instanceof ApiError

export const makeProblem = (
  code: ErrorCode,
  status: number,
  detail?: string,
  errors?: FieldError[],
): ProblemDetails => ({ type: "about:blank", title: code, status, code, detail, errors })

// ---- Access token en memoria ----

let accessToken: string | null = null

export const setAccessToken = (token: string | null): void => {
  accessToken = token
}

export const getAccessToken = (): string | null => accessToken

// ---- Eventos de sesión (los registra SessionProvider) ----

export interface SessionEvents {
  // El auto-refresh emitió un access token nuevo (payload completo de login)
  onRefreshed?: (response: LoginResponse) => void
  // El refresh falló: la sesión ya no es recuperable
  onExpired?: () => void
}

let sessionEvents: SessionEvents = {}

export const setSessionEvents = (events: SessionEvents): void => {
  sessionEvents = events
}

// ---- Parse de errores RFC 7807 ----

const isErrorCode = (value: unknown): value is ErrorCode =>
  typeof value === "string" && (ERROR_CODES as readonly string[]).includes(value)

const fallbackCode = (status: number): ErrorCode => {
  switch (status) {
    case 401:
      return "unauthorized"
    case 403:
      return "forbidden"
    case 404:
      return "not_found"
    case 409:
      return "conflict"
    case 422:
      return "validation_error"
    case 429:
      return "rate_limited"
    default:
      return "internal_error"
  }
}

const parseFieldErrors = (value: unknown): FieldError[] | undefined => {
  if (!Array.isArray(value)) return undefined
  const fields: FieldError[] = []
  for (const entry of value) {
    if (typeof entry !== "object" || entry === null) continue
    const record = entry as Record<string, unknown>
    if (
      typeof record.field === "string" &&
      typeof record.code === "string" &&
      typeof record.message === "string"
    ) {
      fields.push({ field: record.field, code: record.code, message: record.message })
    }
  }
  return fields.length > 0 ? fields : undefined
}

const problemFromResponse = async (response: Response): Promise<ProblemDetails> => {
  let payload: unknown = null
  try {
    payload = await response.json()
  } catch {
    // cuerpo no JSON (proxy, HTML de error…): se usa el fallback por status
  }
  const record =
    typeof payload === "object" && payload !== null ? (payload as Record<string, unknown>) : {}
  const code = isErrorCode(record.code)
    ? record.code
    : isErrorCode(record.title)
      ? record.title
      : fallbackCode(response.status)
  return {
    type: typeof record.type === "string" ? record.type : "about:blank",
    title: typeof record.title === "string" ? record.title : code,
    status: response.status,
    detail: typeof record.detail === "string" ? record.detail : undefined,
    code,
    trace_id: typeof record.trace_id === "string" ? record.trace_id : undefined,
    errors: parseFieldErrors(record.errors),
  }
}

// ---- Auto-refresh único (single-flight) ----
// Todas las requests que reciben 401 mientras hay un refresh en vuelo esperan la
// MISMA promesa y se reintentan una sola vez cuando termina (cola de reintento).

let refreshInFlight: Promise<boolean> | null = null

const doRefresh = async (): Promise<boolean> => {
  try {
    const response = await fetch(`${BASE_URL}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({}),
    })
    if (!response.ok) {
      accessToken = null
      sessionEvents.onExpired?.()
      return false
    }
    const data = (await response.json()) as LoginResponse
    accessToken = data.access_token
    sessionEvents.onRefreshed?.(data)
    return true
  } catch {
    return false
  }
}

export const refreshSession = (): Promise<boolean> => {
  refreshInFlight ??= doRefresh().finally(() => {
    refreshInFlight = null
  })
  return refreshInFlight
}

// ---- Request genérica ----

export type QueryParams = Record<string, string | number | boolean | null | undefined>

export interface ApiRequestOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE"
  body?: unknown
  query?: QueryParams
  signal?: AbortSignal
  // false en endpoints de auth para no entrar en bucle de refresh
  retryOn401?: boolean
}

const buildUrl = (path: string, query?: QueryParams): string => {
  const url = `${BASE_URL}${path}`
  if (!query) return url
  const params = new URLSearchParams()
  for (const [key, value] of Object.entries(query)) {
    if (value === undefined || value === null) continue
    params.set(key, String(value))
  }
  const qs = params.toString()
  return qs ? `${url}?${qs}` : url
}

const execute = async (path: string, options: ApiRequestOptions): Promise<Response> => {
  const headers = new Headers()
  if (options.body !== undefined) headers.set("Content-Type", "application/json")
  if (accessToken) headers.set("Authorization", `Bearer ${accessToken}`)
  return fetch(buildUrl(path, options.query), {
    method: options.method ?? "GET",
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
    signal: options.signal,
  })
}

export const apiFetch = async <T>(path: string, options: ApiRequestOptions = {}): Promise<T> => {
  let response: Response
  try {
    response = await execute(path, options)
  } catch (error) {
    if (error instanceof DOMException && error.name === "AbortError") throw error
    throw new ApiError(makeProblem("internal_error", 0, es.errors.network))
  }

  const canRetry = options.retryOn401 !== false && path !== "/auth/refresh"
  if (response.status === 401 && canRetry) {
    const refreshed = await refreshSession()
    if (refreshed) {
      try {
        response = await execute(path, options)
      } catch {
        throw new ApiError(makeProblem("internal_error", 0, es.errors.network))
      }
    }
  }

  if (!response.ok) throw new ApiError(await problemFromResponse(response))
  if (response.status === 204 || response.status === 205) return undefined as T
  const text = await response.text()
  if (text.length === 0) return undefined as T
  return JSON.parse(text) as T
}

export const api = {
  get: <T>(path: string, options: Omit<ApiRequestOptions, "method" | "body"> = {}) =>
    apiFetch<T>(path, options),
  post: <T>(path: string, body?: unknown, options: Omit<ApiRequestOptions, "method"> = {}) =>
    apiFetch<T>(path, { ...options, method: "POST", body }),
  put: <T>(path: string, body?: unknown, options: Omit<ApiRequestOptions, "method"> = {}) =>
    apiFetch<T>(path, { ...options, method: "PUT", body }),
  patch: <T>(path: string, body?: unknown, options: Omit<ApiRequestOptions, "method"> = {}) =>
    apiFetch<T>(path, { ...options, method: "PATCH", body }),
  delete: <T>(path: string, options: Omit<ApiRequestOptions, "method" | "body"> = {}) =>
    apiFetch<T>(path, { ...options, method: "DELETE" }),
}
